import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion, AnimatePresence, useInView, useReducedMotion, useScroll,
  useTransform, useMotionValue, useSpring, useMotionTemplate,
} from 'framer-motion';
import {
  Phone, MapPin, Clock, Star, Mail, Leaf, Sprout, Sun,
  Snowflake, Flower2, Droplets, Wrench, Ruler, ShieldCheck, BadgeCheck, Calendar,
  ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Check, Menu, X,
  Sparkles, MoveHorizontal, Hammer, Lightbulb, Trees, Search,
} from 'lucide-react';

const SERVICE_ICONS = {
  'Landscape Design': Ruler,
  'Installation': Hammer,
  'Seasonal Maintenance': Wrench,
  'Xeriscape & Native': Sprout,
};

const PROCESS = [
  {
    title: 'Walk & listen',
    body: "We meet on your property, walk every corner, ask about how you actually live outside. Sun, soil, kids, dogs, deer — all of it goes into the brief. 60–90 minutes, no charge.",
    Icon: Search,
    accent: '#16A34A',
  },
  {
    title: 'Design',
    body: '3D mockup with real plants, real materials, real costs. You see it before we break ground. Two free revisions before the final, line-item quote.',
    Icon: Ruler,
    accent: '#0891B2',
  },
  {
    title: 'Install',
    body: 'Our six in-house crews handle everything: hardscape, sod, irrigation, lighting, the tricky stone wall around the elm. One project manager, no subcontractor merry-go-round.',
    Icon: Hammer,
    accent: '#CA8A04',
  },
  {
    title: 'Maintain',
    body: 'Weekly maintenance keeps it looking like the day we left. Optional, never required, easy to cancel. Most clients sign year-one to lock in pricing.',
    Icon: Leaf,
    accent: '#15803D',
  },
];

const SEASONS = [
  {
    key: 'spring',
    label: 'Spring',
    Icon: Flower2,
    accent: '#22C55E',
    accent2: '#FACC15',
    note: 'Cleanup, mulch, irrigation startup, fresh annuals.',
    feature: 'Spring Cleanup',
  },
  {
    key: 'summer',
    label: 'Summer',
    Icon: Sun,
    accent: '#16A34A',
    accent2: '#F97316',
    note: 'Weekly maintenance, smart-water tuning, patio installs.',
    feature: 'Weekly Maintenance',
  },
  {
    key: 'fall',
    label: 'Fall',
    Icon: Leaf,
    accent: '#CA8A04',
    accent2: '#DC2626',
    note: 'Leaf cleanup, fertilization, fall-color planting.',
    feature: 'Spring Cleanup',
  },
  {
    key: 'winter',
    label: 'Winter',
    Icon: Snowflake,
    accent: '#0891B2',
    accent2: '#94A3B8',
    note: 'Holiday lighting, snow removal, design planning for spring.',
    feature: 'Holiday Lighting',
  },
];

const VALUES = [
  'Locally rooted', 'Family-run since 2009', 'Native plant nursery on-site',
  'Six in-house crews', 'No subcontractor merry-go-round', 'Free design consults',
  '4.8★ on Google', '1,200+ properties served', 'Front Range only',
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

// Scoped CSS for leaves, sun pulse, marquee, sheen, draw-in. Class prefix `gl-`
// keeps it from colliding with the other custom demos' keyframes.
const GL_STYLE = `
  @keyframes gl-sheen { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(220%); } }
  .gl-cta-sheen { position: relative; overflow: hidden; isolation: isolate; }
  .gl-cta-sheen::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
    transform: translateX(-120%);
    animation: gl-sheen 5.5s ease-in-out infinite;
    animation-delay: 1.4s;
    pointer-events: none;
  }
  @keyframes gl-leaf-fall {
    0%   { transform: translate3d(0,-6vh,0) rotate(0deg); opacity: 0; }
    8%   { opacity: var(--gl-op, 0.7); }
    50%  { transform: translate3d(var(--gl-sway, 30px), 50vh, 0) rotate(180deg); }
    92%  { opacity: var(--gl-op, 0.7); }
    100% { transform: translate3d(calc(var(--gl-sway, 30px) * -1), 105vh, 0) rotate(360deg); opacity: 0; }
  }
  .gl-leaf { animation: gl-leaf-fall var(--gl-dur, 16s) linear infinite; }
  @keyframes gl-cloud-drift {
    0% { transform: translateX(-12%); }
    100% { transform: translateX(112%); }
  }
  .gl-cloud { animation: gl-cloud-drift var(--gl-cloud-dur, 80s) linear infinite; }
  @keyframes gl-grass-sway {
    0%, 100% { transform: skewX(-3deg); }
    50%      { transform: skewX(3deg); }
  }
  .gl-grass-sway { animation: gl-grass-sway 5.2s ease-in-out infinite; transform-origin: bottom center; }
  @keyframes gl-sun-pulse {
    0%, 100% { transform: scale(1); opacity: 0.65; }
    50%      { transform: scale(1.15); opacity: 0.95; }
  }
  .gl-sun-pulse { animation: gl-sun-pulse 6s ease-in-out infinite; transform-origin: center; }
  @keyframes gl-marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .gl-marquee { animation: gl-marquee 38s linear infinite; }
  @keyframes gl-grow-in {
    0% { transform: scaleY(0); opacity: 0; }
    100% { transform: scaleY(1); opacity: 1; }
  }
  .gl-grow { animation: gl-grow-in 0.9s cubic-bezier(0.22,1,0.36,1) both; transform-origin: bottom center; }
  @keyframes gl-draw {
    0% { stroke-dashoffset: 100; }
    100% { stroke-dashoffset: 0; }
  }
  .gl-draw { stroke-dasharray: 100; animation: gl-draw 1.6s ease-out forwards; }
  @media (prefers-reduced-motion: reduce) {
    .gl-cta-sheen::after, .gl-leaf, .gl-cloud, .gl-grass-sway, .gl-sun-pulse,
    .gl-marquee, .gl-grow, .gl-draw { animation: none !important; }
  }
`;

function useCountUp(target, { active, duration = 1500 } = {}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    if (target === 0) { setValue(0); return undefined; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
}

function parseStat(value) {
  const m = String(value).replace(/,/g, '').match(/^(-?\d+(?:\.\d+)?)(.*)$/);
  if (!m) return null;
  return { num: parseFloat(m[1]), suffix: m[2] || '' };
}

function useTilt(intensity = 10) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const rxs = useSpring(rx, { stiffness: 220, damping: 22 });
  const rys = useSpring(ry, { stiffness: 220, damping: 22 });
  const transform = useMotionTemplate`perspective(900px) rotateX(${rxs}deg) rotateY(${rys}deg)`;
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * intensity);
    rx.set(-py * intensity);
  }
  function onLeave() { rx.set(0); ry.set(0); }
  return { ref, transform, onMove, onLeave };
}

function GreenLeafLandscapingDemo({ demo, sample = false }) {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [tIndex, setTIndex] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const [season, setSeason] = useState('spring');
  const reduceMotion = useReducedMotion();

  const phoneRaw = demo.contact.phone.replace(/[^\d+]/g, '');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!demo.testimonials?.length) return undefined;
    const id = setInterval(() => {
      setTIndex((i) => (i + 1) % demo.testimonials.length);
    }, 7500);
    return () => clearInterval(id);
  }, [demo.testimonials]);

  return (
    <div className="bg-[#FAFBF7] text-[#1F2A1A]" style={{ fontFamily: '"Inter", system-ui, sans-serif' }}>
      <style>{GL_STYLE}</style>
      <SudcoBanner sample={sample} demoName={demo.name} demoIndustry={demo.industry} />
      <TopBar phone={demo.contact.phone} phoneRaw={phoneRaw} address={demo.contact.address} />
      <Header
        demoName={demo.name}
        phone={demo.contact.phone}
        phoneRaw={phoneRaw}
        scrolled={scrolled}
        navOpen={navOpen}
        setNavOpen={setNavOpen}
        fontDisplay={demo.fontDisplay}
      />
      <Hero demo={demo} fontDisplay={demo.fontDisplay} reduceMotion={reduceMotion} />
      <ValueMarquee />
      <Stats stats={demo.stats} fontDisplay={demo.fontDisplay} />
      <Services services={demo.services} fontDisplay={demo.fontDisplay} />
      <SeasonsToggle season={season} setSeason={setSeason} fontDisplay={demo.fontDisplay} images={demo.gallery} />
      <Process fontDisplay={demo.fontDisplay} reduceMotion={reduceMotion} />
      <BeforeAfter images={demo.gallery} fontDisplay={demo.fontDisplay} />
      <About about={demo.about} cover={demo.cover} fontDisplay={demo.fontDisplay} />
      <Pricing featured={demo.featured} fontDisplay={demo.fontDisplay} />
      <Gallery images={demo.gallery} fontDisplay={demo.fontDisplay} />
      <Testimonials testimonials={demo.testimonials} index={tIndex} setIndex={setTIndex} fontDisplay={demo.fontDisplay} />
      <FaqSection faq={demo.faq} openFaq={openFaq} setOpenFaq={setOpenFaq} fontDisplay={demo.fontDisplay} />
      <CtaBanner phone={demo.contact.phone} phoneRaw={phoneRaw} fontDisplay={demo.fontDisplay} />
      <Contact contact={demo.contact} hours={demo.hours} fontDisplay={demo.fontDisplay} />
      <Footer demo={demo} fontDisplay={demo.fontDisplay} />
    </div>
  );
}

function SudcoBanner({ sample, demoName, demoIndustry }) {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <div className="bg-[#0B0F19]/90 border border-[#2A3142] backdrop-blur-md rounded-full px-5 py-3 shadow-[0_0_60px_-15px_rgba(99,102,241,0.5)] flex items-center gap-4 text-sm">
        <span className="text-[#8B92A8] hidden sm:inline">
          {sample
            ? <>Like this? Get one for your <span className="text-[#F5F6FA] font-medium">{demoIndustry || 'business'}</span> — <span className="text-[#6366F1] font-semibold">$800</span></>
            : <>Free demo for <span className="text-[#F5F6FA] font-medium">{demoName}</span> by <span className="text-[#6366F1] font-semibold">Sudco Solutions</span></>
          }
        </span>
        <Link to="/" className="flex items-center gap-1.5 text-[#F5F6FA] font-semibold hover:text-[#06B6D4] transition-colors whitespace-nowrap">
          <ArrowLeft size={14} /> Back to Sudco
        </Link>
      </div>
    </div>
  );
}

function TopBar({ phone, phoneRaw, address }) {
  return (
    <div className="hidden md:block bg-emerald-950 text-emerald-100 text-xs">
      <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <MapPin size={12} className="text-amber-400" />{address}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Trees size={12} className="text-amber-400" />Front Range, Colorado
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-amber-400" />Licensed · Insured · Bonded
          </span>
        </div>
        <div className="flex items-center gap-5">
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={12} className="text-amber-400" />Booking spring '26 now
          </span>
          <a href={`tel:${phoneRaw}`} className="inline-flex items-center gap-1.5 text-white font-semibold hover:text-amber-300">
            <Phone size={12} className="text-amber-400" />{phone}
          </a>
        </div>
      </div>
    </div>
  );
}

function GreenLeafMark({ size = 20, className = '' }) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} className={className} aria-hidden="true">
      <path
        d="M16 4 C 9 7, 6 13, 6 19 C 6 24, 10 27, 14 27 L 14 21 C 11 20, 9 18, 9 14 L 12 14 C 12 17, 13 18, 14 19 L 14 12 L 16 12 L 16 19 C 17 18, 18 17, 18 14 L 21 14 C 21 18, 19 20, 16 21 L 16 27 C 22 27, 26 24, 26 19 C 26 13, 23 7, 16 4 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function Header({ demoName, phone, phoneRaw, scrolled, navOpen, setNavOpen, fontDisplay }) {
  const links = [
    { href: '#services', label: 'Services' },
    { href: '#process', label: 'Process' },
    { href: '#transform', label: 'Before / After' },
    { href: '#packages', label: 'Packages' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur shadow-[0_1px_0_0_rgba(0,0,0,0.06),0_8px_24px_-12px_rgba(0,0,0,0.12)]'
          : 'bg-white border-b border-emerald-100/60'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5 group">
          <span className="w-10 h-10 md:w-11 md:h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center text-white shadow-md group-hover:rotate-12 transition-transform">
            <GreenLeafMark size={20} />
          </span>
          <span
            className="font-extrabold text-lg md:text-2xl tracking-tight text-emerald-900"
            style={{ fontFamily: fontDisplay }}
          >
            {demoName}
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-emerald-900/80">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="relative group">
              <span className="group-hover:text-emerald-700 transition-colors">{l.label}</span>
              <span className="absolute left-0 -bottom-1 h-0.5 w-full origin-left scale-x-0 bg-amber-500 transition-transform duration-300 group-hover:scale-x-100" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 text-emerald-800 font-bold text-base hover:text-emerald-900"
          >
            <Phone size={16} />{phone}
          </a>
          <a
            href="#contact"
            className="gl-cta-sheen inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all"
          >
            Free design consult <ArrowRight size={14} />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setNavOpen((v) => !v)}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          className="lg:hidden p-2 -mr-2 text-emerald-900"
        >
          {navOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-emerald-100 bg-white"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setNavOpen(false)}
                  className="px-3 py-2.5 rounded-md font-semibold text-emerald-900 hover:bg-emerald-50"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-2 grid grid-cols-2 gap-2">
                <a
                  href={`tel:${phoneRaw}`}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg border border-emerald-700 text-emerald-800 text-sm font-bold"
                >
                  <Phone size={14} />Call
                </a>
                <a
                  href="#contact"
                  onClick={() => setNavOpen(false)}
                  className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-emerald-700 text-white text-sm font-bold"
                >
                  Consult
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

const LEAF_PATHS = [
  'M12,2 C7,4 4,9 4,14 C4,19 7,22 12,22 C12,18 10,15 8,14 C12,15 14,17 14,22 C19,22 22,19 22,14 C22,9 19,4 14,2 C13,5 13,8 14,12 C13,8 12,4 12,2 Z',
  'M12,3 C8,3 5,8 5,13 C5,18 8,21 12,21 C16,21 19,18 19,13 C19,8 16,3 12,3 Z M12,7 L12,18 M9,11 L12,13 L15,11',
  'M16,3 C10,5 5,10 5,16 C5,20 8,22 12,22 C18,22 22,17 22,11 C22,7 19,4 16,3 Z M9,18 C12,15 15,12 18,9',
];

function LeafField({ count = 18, scope = 'page' }) {
  const items = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        left: `${(i * 47) % 100}%`,
        size: 14 + ((i * 7) % 18),
        delay: -((i * 1.9) % 18),
        dur: 14 + ((i * 5) % 14),
        op: 0.45 + ((i * 11) % 4) * 0.12,
        sway: ((i % 2 === 0 ? 1 : -1) * (20 + ((i * 13) % 50))) + 'px',
        hue: i % 3 === 0 ? '#16A34A' : i % 3 === 1 ? '#CA8A04' : '#A16207',
        path: LEAF_PATHS[i % LEAF_PATHS.length],
      })),
    [count],
  );
  return (
    <div
      className={`pointer-events-none ${scope === 'page' ? 'fixed' : 'absolute'} inset-0 overflow-hidden`}
      aria-hidden="true"
    >
      {items.map((p, i) => (
        <span
          key={i}
          className="gl-leaf absolute"
          style={{
            left: p.left,
            top: '-6vh',
            width: p.size,
            height: p.size,
            color: p.hue,
            '--gl-dur': `${p.dur}s`,
            '--gl-op': p.op,
            '--gl-sway': p.sway,
            animationDelay: `${p.delay}s`,
          }}
        >
          <svg viewBox="0 0 24 24" width="100%" height="100%" fill="currentColor">
            <path d={p.path} />
          </svg>
        </span>
      ))}
    </div>
  );
}

function ParallaxScene() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const sky = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const farHills = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const midHills = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);
  const nearHills = useTransform(scrollYProgress, [0, 1], ['0%', '90%']);

  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden" aria-hidden="true">
      <motion.div className="absolute inset-x-0 top-0 h-full" style={{ y: sky }}>
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, #BFDBFE 0%, #DCFCE7 55%, #ECFCCB 100%)',
          }}
        />
        <div className="absolute top-8 right-[12%]">
          <div className="gl-sun-pulse w-32 h-32 md:w-44 md:h-44 rounded-full bg-gradient-to-br from-amber-200 via-amber-300 to-amber-100 blur-2xl" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 shadow-[0_0_70px_30px_rgba(252,211,77,0.45)]" />
          </div>
        </div>
        <div className="absolute top-12 left-0 right-0 h-24 overflow-hidden">
          <div
            className="gl-cloud absolute top-0 left-0 flex items-center gap-32"
            style={{ '--gl-cloud-dur': '90s' }}
          >
            <CloudShape opacity={0.85} />
            <CloudShape opacity={0.7} small />
            <CloudShape opacity={0.85} />
            <CloudShape opacity={0.7} small />
          </div>
        </div>
        <div className="absolute top-24 left-0 right-0 h-20 overflow-hidden">
          <div
            className="gl-cloud absolute top-0 left-0 flex items-center gap-40"
            style={{ '--gl-cloud-dur': '140s' }}
          >
            <CloudShape opacity={0.6} small />
            <CloudShape opacity={0.5} />
            <CloudShape opacity={0.6} small />
          </div>
        </div>
      </motion.div>

      <motion.div className="absolute inset-x-0 bottom-0" style={{ y: farHills }}>
        <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full h-48 md:h-64 block">
          <path
            d="M0,140 C200,90 400,160 700,110 C1000,70 1200,140 1440,100 L1440,200 L0,200 Z"
            fill="#86EFAC"
            opacity="0.7"
          />
        </svg>
      </motion.div>
      <motion.div className="absolute inset-x-0 bottom-0" style={{ y: midHills }}>
        <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full h-48 md:h-64 block">
          <path
            d="M0,160 C300,120 500,180 800,130 C1100,90 1300,170 1440,140 L1440,200 L0,200 Z"
            fill="#4ADE80"
            opacity="0.85"
          />
        </svg>
      </motion.div>
      <motion.div className="absolute inset-x-0 bottom-0" style={{ y: nearHills }}>
        <svg viewBox="0 0 1440 220" preserveAspectRatio="none" className="w-full h-52 md:h-72 block">
          <path
            d="M0,180 C200,140 500,200 800,160 C1100,120 1300,190 1440,170 L1440,220 L0,220 Z"
            fill="#15803D"
          />
          <g fill="#166534">
            {[120, 360, 600, 980, 1240].map((x, i) => (
              <g key={x} transform={`translate(${x},${165 - (i % 2) * 5})`}>
                <path d="M0,0 C-12,-30 12,-30 0,0 M-2,0 L2,0 L2,18 L-2,18 Z" />
              </g>
            ))}
          </g>
          <g className="gl-grass-sway" fill="#22C55E">
            {Array.from({ length: 30 }).map((_, i) => (
              <path
                key={i}
                d={`M${(i * 50) + 10},220 L${(i * 50) + 15},${190 - (i % 3) * 6} L${(i * 50) + 20},220 Z`}
              />
            ))}
          </g>
        </svg>
      </motion.div>
    </div>
  );
}

function CloudShape({ opacity = 0.85, small = false }) {
  const w = small ? 80 : 130;
  const h = small ? 30 : 48;
  return (
    <svg viewBox="0 0 130 48" width={w} height={h} className="flex-shrink-0">
      <g fill="white" opacity={opacity}>
        <ellipse cx="30" cy="32" rx="22" ry="14" />
        <ellipse cx="60" cy="24" rx="28" ry="18" />
        <ellipse cx="92" cy="32" rx="22" ry="14" />
      </g>
    </svg>
  );
}

function Hero({ demo, fontDisplay, reduceMotion }) {
  return (
    <section id="top" className="relative overflow-hidden min-h-[88vh] flex items-end">
      <ParallaxScene />
      {!reduceMotion && <LeafField count={14} scope="section" />}

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-24 md:pt-24 md:pb-32">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          className="max-w-3xl"
        >
          <motion.div
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-5 shadow-sm"
          >
            <Sparkles size={14} className="text-amber-500" />
            Free design consult — booking spring '26
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="font-extrabold text-5xl sm:text-6xl md:text-7xl leading-[1.0] tracking-tight text-emerald-950 mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            Yards that look
            <span className="block italic text-emerald-700">
              like Colorado
              <svg
                viewBox="0 0 220 12"
                className="absolute hidden md:block w-56 h-3 mt-1"
                aria-hidden="true"
              >
                <path
                  d="M2,9 C40,-2 100,12 218,3"
                  stroke="#CA8A04"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                  className="gl-draw"
                />
              </svg>
            </span>
            <span className="block text-emerald-900">— not New Jersey.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-lg md:text-xl text-emerald-900/80 leading-relaxed max-w-2xl mb-8">
            {demo.tagline} Family-run since 2009. Six in-house crews. A 4-acre nursery growing native plants the deer leave alone.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-9 text-sm text-emerald-900/80">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="fill-amber-500 text-amber-500" />
                ))}
              </span>
              <strong>4.8</strong> · 320+ Google reviews
            </span>
            <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-emerald-700" /> Licensed & bonded</span>
            <span className="inline-flex items-center gap-1.5"><Trees size={14} className="text-emerald-700" /> 90+ native species on-site</span>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            <a
              href="#contact"
              className="gl-cta-sheen inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-base font-bold shadow-xl shadow-emerald-900/20 transition-all hover:scale-[1.02]"
            >
              <Sprout size={18} />Book a free design consult
            </a>
            <a
              href="#transform"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/90 backdrop-blur hover:bg-white text-emerald-800 text-base font-semibold border border-emerald-200 shadow-sm"
            >
              See a transformation <ArrowRight size={16} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function ValueMarquee() {
  const items = [...VALUES, ...VALUES];
  return (
    <div className="bg-emerald-900 text-emerald-50 overflow-hidden border-y border-emerald-800/40">
      <div className="gl-marquee py-3 flex items-center gap-10 whitespace-nowrap text-sm font-bold uppercase tracking-wider">
        {items.map((v, i) => (
          <span key={i} className="inline-flex items-center gap-3 flex-shrink-0">
            <Leaf size={14} className="text-amber-400" />
            <span>{v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Stats({ stats, fontDisplay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  if (!stats?.length) return null;
  return (
    <section ref={ref} className="bg-[#FAFBF7] relative overflow-hidden">
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 50%, #DCFCE7 0%, transparent 40%), radial-gradient(circle at 80% 50%, #FEF3C7 0%, transparent 40%)',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s, i) => (
            <StatCard key={s.label} stat={s} active={inView} fontDisplay={fontDisplay} delay={i * 0.08} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, active, fontDisplay, delay }) {
  const parsed = parseStat(stat.value);
  const numeric = useCountUp(parsed?.num ?? 0, { active });
  const display = parsed
    ? `${parsed.num >= 1000 ? Math.round(numeric).toLocaleString() : numeric.toFixed(parsed.num % 1 ? 1 : 0)}${parsed.suffix}`
    : stat.value;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={active ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className="relative group"
    >
      <div className="absolute -top-2 left-6 w-10 h-10 rounded-b-full bg-gradient-to-b from-emerald-600 to-emerald-700 transform-gpu transition-transform group-hover:scale-110 group-hover:-translate-y-1" />
      <Leaf className="absolute -top-1 left-7 text-emerald-50 transition-transform group-hover:rotate-12" size={20} />
      <div className="rounded-2xl border border-emerald-100 bg-white p-6 md:p-7 text-center shadow-sm group-hover:shadow-md transition-shadow">
        <div
          className="font-extrabold text-4xl md:text-5xl text-emerald-900 leading-none tabular-nums"
          style={{ fontFamily: fontDisplay }}
        >
          {display}
        </div>
        <div className="mt-3 text-xs md:text-sm font-semibold text-emerald-800/70 uppercase tracking-wider">
          {stat.label}
        </div>
      </div>
    </motion.div>
  );
}

function ServiceCard({ service, index, fontDisplay }) {
  const Icon = SERVICE_ICONS[service.title] || Leaf;
  const tilt = useTilt(8);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      ref={tilt.ref}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
      className="relative group"
    >
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-300/0 via-emerald-300/40 to-amber-300/40 opacity-0 group-hover:opacity-100 blur-xl transition-opacity" aria-hidden="true" />
      <a
        href="#contact"
        className="relative block rounded-2xl bg-white border border-emerald-100 p-7 hover:border-emerald-300 transition-colors overflow-hidden h-full"
        style={{ transform: 'translateZ(0)' }}
      >
        <div className="absolute -right-4 -top-4 w-32 h-32 rounded-full bg-emerald-50 group-hover:bg-emerald-100 transition-colors" aria-hidden="true" />
        <div className="absolute -right-6 -top-6 opacity-10 text-emerald-700 group-hover:opacity-20 group-hover:rotate-12 transition-all duration-500">
          <Icon size={120} />
        </div>
        <div className="relative">
          <span className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white items-center justify-center mb-5 shadow-md group-hover:scale-110 group-hover:rotate-6 transition-transform">
            <Icon size={22} />
          </span>
          <h3
            className="font-extrabold text-xl text-emerald-950 mb-2 group-hover:text-emerald-700 transition-colors"
            style={{ fontFamily: fontDisplay }}
          >
            {service.title}
          </h3>
          <p className="text-emerald-900/70 leading-relaxed text-[15px] mb-5">{service.desc}</p>
          <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700">
            Get a quote
            <ArrowRight
              size={14}
              className="transition-transform group-hover:translate-x-1"
            />
          </span>
        </div>
      </a>
    </motion.div>
  );
}

function Services({ services, fontDisplay }) {
  return (
    <section id="services" className="relative py-20 md:py-28 bg-[#FAFBF7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">What we do</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Design, install,
            <span className="italic text-emerald-700"> maintain</span>.
          </h2>
          <p className="mt-4 text-emerald-900/70 leading-relaxed text-lg">
            Soup-to-nuts. We design the yard, build the yard, then keep it looking like the day we left.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} fontDisplay={fontDisplay} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SeasonsToggle({ season, setSeason, fontDisplay, images }) {
  const cur = SEASONS.find((s) => s.key === season) || SEASONS[0];
  const Cur = cur.Icon;
  return (
    <section
      className="relative py-20 md:py-28 overflow-hidden transition-colors duration-700"
      style={{
        background: `linear-gradient(180deg, ${cur.accent}10 0%, ${cur.accent2}08 100%)`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center">
          <div>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-2 transition-colors"
              style={{ color: cur.accent }}
            >
              Year-round care
            </p>
            <h2
              className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight mb-5"
              style={{ fontFamily: fontDisplay }}
            >
              Twelve months,{' '}
              <span className="italic transition-colors" style={{ color: cur.accent }}>
                four seasons
              </span>
              , one crew.
            </h2>
            <p className="text-emerald-900/70 leading-relaxed text-lg mb-7">
              Front Range yards have very different needs in February than in July. Same crew, same project manager, all 12 months — so the work compounds instead of starting over.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-7">
              {SEASONS.map((s) => {
                const SIcon = s.Icon;
                const active = s.key === season;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSeason(s.key)}
                    className={`relative p-4 rounded-2xl border transition-all overflow-hidden ${
                      active
                        ? 'border-transparent text-white shadow-lg scale-105'
                        : 'bg-white border-emerald-100 hover:border-emerald-300 text-emerald-900'
                    }`}
                    style={active ? { background: `linear-gradient(135deg, ${s.accent}, ${s.accent2})` } : undefined}
                  >
                    <SIcon size={22} className={`mb-2 ${active ? 'text-white' : 'text-emerald-700'}`} />
                    <div className="font-bold text-sm">{s.label}</div>
                    {active && (
                      <motion.span
                        layoutId="season-glow"
                        className="absolute inset-0 rounded-2xl ring-2 ring-white/40 pointer-events-none"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={season}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl bg-white border border-emerald-100 p-5 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-md"
                    style={{ background: `linear-gradient(135deg, ${cur.accent}, ${cur.accent2})` }}
                  >
                    <Cur size={20} />
                  </span>
                  <div>
                    <div
                      className="font-extrabold text-lg text-emerald-950"
                      style={{ fontFamily: fontDisplay }}
                    >
                      {cur.label} focus
                    </div>
                    <div className="text-sm text-emerald-900/70">{cur.note}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={season}
                initial={{ opacity: 0, scale: 0.96, rotate: -1 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 1.04, rotate: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] sm:aspect-[5/4] rounded-3xl overflow-hidden shadow-2xl"
                style={{ boxShadow: `0 30px 80px -20px ${cur.accent}40` }}
              >
                <img
                  src={images[SEASONS.findIndex((s) => s.key === season) % images.length]}
                  alt={`${cur.label} yard`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 mix-blend-multiply transition-opacity"
                  style={{ background: `linear-gradient(180deg, transparent 50%, ${cur.accent}55 100%)` }}
                />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-xs font-bold uppercase tracking-wider opacity-90">{cur.label} install</div>
                  <div className="text-2xl font-extrabold mt-1" style={{ fontFamily: fontDisplay }}>
                    {cur.feature}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-4 -right-4 w-20 h-20 rounded-2xl bg-white shadow-xl border border-emerald-100 flex items-center justify-center"
            >
              <span
                className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                style={{ background: `linear-gradient(135deg, ${cur.accent}, ${cur.accent2})` }}
              >
                <Cur size={22} />
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process({ fontDisplay, reduceMotion }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const lineHeight = useTransform(scrollYProgress, [0.15, 0.85], ['0%', '100%']);
  return (
    <section id="process" ref={ref} className="relative py-20 md:py-28 bg-emerald-950 text-emerald-50 overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 80% 20%, #15803D 0%, transparent 40%), radial-gradient(circle at 10% 80%, #CA8A04 0%, transparent 35%)',
      }} />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-2">How we work</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            From dirt to{' '}
            <span className="italic text-amber-300">finished yard</span>{' '}
            in four steps.
          </h2>
        </div>

        <div className="relative grid md:grid-cols-2 gap-10 md:gap-x-20 md:gap-y-24">
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-emerald-800/60">
            {!reduceMotion && (
              <motion.div
                style={{ height: lineHeight }}
                className="absolute left-0 top-0 w-full bg-gradient-to-b from-amber-400 via-emerald-400 to-emerald-600"
              />
            )}
          </div>

          {PROCESS.map((step, i) => (
            <ProcessStep
              key={step.title}
              step={step}
              index={i}
              fontDisplay={fontDisplay}
              align={i % 2 === 0 ? 'left' : 'right'}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessStep({ step, index, fontDisplay, align }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const Icon = step.Icon;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: align === 'left' ? -30 : 30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${align === 'right' ? 'md:col-start-2' : ''}`}
    >
      <div className="hidden md:block absolute top-6 -translate-y-1/2 z-10"
        style={{ [align === 'left' ? 'right' : 'left']: '-2.5rem' }}
      >
        <div
          className="w-5 h-5 rounded-full ring-4 ring-emerald-950 transition-all"
          style={{ background: step.accent, boxShadow: inView ? `0 0 24px ${step.accent}` : undefined }}
        />
      </div>
      <div className="rounded-2xl bg-emerald-900/40 border border-emerald-700/30 backdrop-blur p-6 md:p-7 hover:bg-emerald-900/60 transition-colors">
        <div className="flex items-center gap-3 mb-4">
          <span
            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg"
            style={{ background: `linear-gradient(135deg, ${step.accent}, ${step.accent}cc)` }}
          >
            <Icon size={22} />
          </span>
          <span
            className="font-extrabold text-5xl tabular-nums leading-none"
            style={{ fontFamily: fontDisplay, color: step.accent, opacity: 0.55 }}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
        <h3
          className="font-extrabold text-2xl md:text-3xl text-white mb-3 leading-tight"
          style={{ fontFamily: fontDisplay }}
        >
          {step.title}
        </h3>
        <p className="text-emerald-100/80 text-[15px] leading-relaxed">{step.body}</p>
      </div>
    </motion.div>
  );
}

function BeforeAfter({ images, fontDisplay }) {
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  function setFromClientX(clientX) {
    const el = containerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPct(Math.max(0, Math.min(100, p)));
  }

  useEffect(() => {
    function move(e) {
      if (!dragging.current) return;
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(cx);
    }
    function up() { dragging.current = false; }
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, []);

  const before = images?.[2] || images?.[0];
  const after = images?.[0];

  return (
    <section id="transform" className="relative py-20 md:py-28 bg-[#FAFBF7] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">See the work</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Drag the slider —
            <span className="italic text-emerald-700"> watch the weed patch disappear</span>.
          </h2>
          <p className="mt-4 text-emerald-900/70 leading-relaxed">
            One real client, Fort Collins. 1,500 sq ft of dead lawn converted to xeriscape with native grasses, flagstone path, and drip irrigation. Six weeks of work.
          </p>
        </div>

        <div
          ref={containerRef}
          className="relative aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden shadow-2xl select-none cursor-ew-resize touch-none"
          onMouseDown={(e) => { dragging.current = true; setFromClientX(e.clientX); }}
          onTouchStart={(e) => { dragging.current = true; setFromClientX(e.touches[0].clientX); }}
        >
          <img src={after} alt="After: xeriscaped yard" className="absolute inset-0 w-full h-full object-cover" />
          <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-700 text-white text-xs font-extrabold uppercase tracking-wider shadow-lg">
            <Sprout size={12} />After
          </span>

          <div className="absolute inset-0 overflow-hidden" style={{ width: `${pct}%` }}>
            <img
              src={before}
              alt="Before: dead lawn"
              className="absolute inset-0 w-screen max-w-none h-full object-cover"
              style={{ width: containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : '100%' }}
            />
            <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 text-white text-xs font-extrabold uppercase tracking-wider shadow-lg">
              <Droplets size={12} />Before
            </span>
          </div>

          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_24px_rgba(0,0,0,0.4)]"
            style={{ left: `calc(${pct}% - 2px)` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-14 h-14 rounded-full bg-white shadow-[0_8px_28px_rgba(0,0,0,0.3)] flex items-center justify-center text-emerald-800 cursor-ew-resize border-2 border-emerald-100"
            style={{ left: `${pct}%` }}
            aria-label="Drag to compare"
          >
            <MoveHorizontal size={24} />
          </div>

          <div className="hidden md:block absolute bottom-5 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/90 backdrop-blur text-emerald-900 text-xs font-bold shadow border border-emerald-100">
            ← drag to compare →
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          <StatRow icon={Droplets} label="Water savings" value="60%" />
          <StatRow icon={Calendar} label="Project duration" value="6 weeks" />
          <StatRow icon={Sprout} label="Native species used" value="14" />
        </div>
      </div>
    </section>
  );
}

function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-emerald-100 bg-white p-4 flex items-center gap-3">
      <span className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-emerald-900/60 font-bold">{label}</div>
        <div className="text-emerald-950 font-extrabold text-lg">{value}</div>
      </div>
    </div>
  );
}

function About({ about, cover, fontDisplay }) {
  const tilt = useTilt(6);
  return (
    <section className="relative py-20 md:py-28 bg-emerald-50/40 overflow-hidden">
      <div className="absolute -left-40 top-1/4 w-96 h-96 rounded-full bg-emerald-200/40 blur-3xl pointer-events-none" />
      <div className="absolute -right-32 bottom-0 w-80 h-80 rounded-full bg-amber-200/40 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Our story</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            {about?.heading}
          </h2>
          {about?.body?.split('\n\n').map((para, i) => (
            <p key={i} className="text-emerald-900/80 leading-relaxed mb-4 text-[15px] md:text-base">{para}</p>
          ))}
          {about?.signature && (
            <div className="mt-6 flex items-center gap-3">
              <span className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex items-center justify-center font-extrabold">
                MH
              </span>
              <div>
                <p
                  className="text-emerald-900 font-bold leading-tight"
                  style={{ fontFamily: fontDisplay }}
                >
                  {about.signature}
                </p>
                <p className="text-xs text-emerald-700/70 uppercase tracking-wider mt-0.5">Second-generation owner</p>
              </div>
            </div>
          )}
        </div>

        <motion.div
          ref={tilt.ref}
          onMouseMove={tilt.onMove}
          onMouseLeave={tilt.onLeave}
          style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
          className="relative"
        >
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl" style={{ transform: 'translateZ(40px)' }}>
            <img src={cover} alt="GreenLeaf crew at work" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/60 via-transparent to-transparent" />
          </div>
          <div
            className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-emerald-100 p-5 max-w-xs"
            style={{ transform: 'translateZ(70px)' }}
          >
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-amber-600 mb-1">
              <Trees size={14} />Front Range native
            </div>
            <p className="text-emerald-950 text-sm font-semibold leading-snug">
              "We grow the plants we install. 4 acres, 90+ species, all Colorado-hardy."
            </p>
          </div>
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [-3, 3, -3] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -top-5 -right-3"
            style={{ transform: 'translateZ(80px)' }}
          >
            <div className="w-20 h-20 rounded-2xl bg-amber-400 shadow-xl flex items-center justify-center text-emerald-950 rotate-6">
              <div className="text-center">
                <div className="text-2xl font-extrabold leading-none" style={{ fontFamily: fontDisplay }}>15</div>
                <div className="text-[9px] font-extrabold uppercase tracking-wider mt-0.5">Years</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Pricing({ featured, fontDisplay }) {
  if (!featured?.items?.length) return null;
  return (
    <section id="packages" className="py-20 md:py-28 bg-[#FAFBF7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-2xl mb-14">
          {featured.kicker && (
            <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">{featured.kicker}</p>
          )}
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            {featured.heading}
          </h2>
          <p className="mt-4 text-emerald-900/70 leading-relaxed text-lg">
            Real prices. Real scope. Most clients book 2–3 of these as a starting point and grow from there.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.items.map((item, i) => (
            <PricingCard key={item.name} item={item} index={i} fontDisplay={fontDisplay} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingCard({ item, index, fontDisplay }) {
  const tilt = useTilt(4);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      ref={tilt.ref}
      onMouseMove={tilt.onMove}
      onMouseLeave={tilt.onLeave}
      style={{ transform: tilt.transform, transformStyle: 'preserve-3d' }}
      className={`relative rounded-2xl border p-6 transition-shadow hover:shadow-xl ${
        item.tag
          ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-white shadow-md'
          : 'border-emerald-100 bg-white'
      }`}
    >
      {item.tag && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500 text-white text-[11px] font-extrabold uppercase tracking-wider shadow">
          <Sparkles size={11} />{item.tag}
        </span>
      )}
      <h3
        className="font-extrabold text-xl text-emerald-950 leading-tight pr-4"
        style={{ fontFamily: fontDisplay }}
      >
        {item.name}
      </h3>
      <p className="mt-2 text-emerald-900/70 text-sm leading-relaxed min-h-[3.5rem]">{item.desc}</p>
      <div className="mt-5 pt-4 border-t border-emerald-100 flex items-end justify-between gap-3">
        <span
          className="font-extrabold text-2xl text-emerald-700 leading-none"
          style={{ fontFamily: fontDisplay }}
        >
          {item.price}
        </span>
        <a
          href="#contact"
          className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900"
        >
          Book this <ArrowRight size={12} />
        </a>
      </div>
    </motion.div>
  );
}

const GALLERY_TAGS = [
  { key: 'all', label: 'All' },
  { key: 'design', label: 'Design' },
  { key: 'install', label: 'Install' },
  { key: 'xeriscape', label: 'Xeriscape' },
];

function Gallery({ images, fontDisplay }) {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const captions = [
    { caption: 'Backyard transformation, Fort Collins', tag: 'install' },
    { caption: 'Native xeriscape with flagstone path', tag: 'xeriscape' },
    { caption: '3D design mockup → finished install', tag: 'design' },
    { caption: 'Front-yard renovation with retaining wall', tag: 'install' },
  ];
  const items = images.map((src, i) => ({ src, ...captions[i % captions.length] }));
  const filtered = filter === 'all' ? items : items.filter((it) => it.tag === filter);

  return (
    <section id="gallery" className="relative py-20 md:py-28 bg-emerald-50/40">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">The portfolio</p>
            <h2
              className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight"
              style={{ fontFamily: fontDisplay }}
            >
              Recent work,
              <span className="italic text-emerald-700"> not stock photos.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {GALLERY_TAGS.map((g) => {
              const active = filter === g.key;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setFilter(g.key)}
                  className={`relative px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                    active
                      ? 'bg-emerald-700 text-white shadow-md'
                      : 'bg-white text-emerald-900 border border-emerald-200 hover:border-emerald-400'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="gallery-pill"
                      className="absolute inset-0 rounded-full bg-emerald-700"
                      style={{ zIndex: -1 }}
                    />
                  )}
                  <span className="relative">{g.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <AnimatePresence>
            {filtered.map((it, i) => (
              <motion.button
                key={it.src + filter}
                layout
                type="button"
                onClick={() => setLightbox(images.indexOf(it.src))}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className={`group relative rounded-2xl overflow-hidden bg-emerald-100 ${
                  i === 0 ? 'col-span-2 md:row-span-2 md:col-span-2 aspect-[4/3] md:aspect-auto' : 'aspect-square'
                }`}
              >
                <img
                  src={it.src}
                  alt={it.caption}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 via-transparent to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-amber-400 text-emerald-950 text-[10px] font-extrabold uppercase tracking-wider mb-1">
                    {it.tag}
                  </span>
                  <p className="text-xs md:text-sm font-semibold leading-snug">{it.caption}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-emerald-950/95 backdrop-blur flex items-center justify-center p-4 md:p-8"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              key={images[lightbox]}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              src={images[lightbox]}
              alt=""
              className="max-w-5xl max-h-[80vh] object-contain rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 text-white text-sm">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="font-semibold">{lightbox + 1} / {images.length}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }}
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Testimonials({ testimonials, index, setIndex, fontDisplay }) {
  if (!testimonials?.length) return null;
  const total = testimonials.length;
  const t = testimonials[index];
  return (
    <section className="py-20 md:py-28 bg-[#FAFBF7] relative overflow-hidden">
      <div className="absolute top-12 left-8 text-emerald-100" aria-hidden="true">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <text x="0" y="100" fontSize="180" fontFamily="Fraunces, serif" fill="currentColor">"</text>
        </svg>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Word of mouth</p>
        <h2
          className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight mb-3"
          style={{ fontFamily: fontDisplay }}
        >
          What clients say.
        </h2>
        <div className="flex items-center justify-center gap-1 mb-12">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={20} className="fill-amber-500 text-amber-500" />
          ))}
          <span className="ml-2 text-emerald-900/70 font-semibold">4.8 on Google · 320+ reviews</span>
        </div>

        <div className="relative min-h-[260px] md:min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <p
                className="text-2xl md:text-3xl leading-relaxed text-emerald-950 max-w-3xl mx-auto italic"
                style={{ fontFamily: fontDisplay }}
              >
                "{t.quote}"
              </p>
              <footer className="mt-7 text-sm text-emerald-900/70 font-semibold">
                — {t.author}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + total) % total)}
            aria-label="Previous review"
            className="w-11 h-11 rounded-full border border-emerald-200 hover:border-emerald-500 hover:text-emerald-700 flex items-center justify-center transition-colors bg-white"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex items-center gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Review ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-8 bg-emerald-700' : 'w-2 bg-emerald-200 hover:bg-emerald-300'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % total)}
            aria-label="Next review"
            className="w-11 h-11 rounded-full border border-emerald-200 hover:border-emerald-500 hover:text-emerald-700 flex items-center justify-center transition-colors bg-white"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FaqSection({ faq, openFaq, setOpenFaq, fontDisplay }) {
  return (
    <section id="faq" className="py-20 md:py-28 bg-emerald-50/40 border-y border-emerald-100">
      <div className="max-w-5xl mx-auto px-4 md:px-6 grid md:grid-cols-[1fr_1.4fr] gap-10 md:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Common questions</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Asked &{' '}
            <span className="italic text-emerald-700">answered</span>.
          </h2>
          <p className="mt-3 text-emerald-900/70 leading-relaxed">
            Don't see yours? <a href="#contact" className="text-emerald-700 font-bold underline">Send a note</a> — we reply within a business day.
          </p>
        </div>
        <div className="space-y-2">
          {faq.map((item, i) => {
            const open = openFaq === i;
            return (
              <div
                key={item.q}
                className={`rounded-xl border transition-all ${
                  open ? 'bg-white border-emerald-300 shadow-sm' : 'bg-white border-emerald-100'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="font-bold text-emerald-950 text-[15px]">{item.q}</span>
                  <ChevronDown
                    size={18}
                    className={`flex-shrink-0 text-emerald-700 transition-transform ${open ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-emerald-900/80 leading-relaxed text-[15px]">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner({ phone, phoneRaw, fontDisplay }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  }
  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 text-white"
    >
      <div
        className="absolute inset-0 transition-opacity pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${pos.x}% ${pos.y}%, rgba(252,211,77,0.35), transparent 40%)`,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 opacity-30" aria-hidden="true" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 100%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 80% 0%, rgba(255,255,255,0.15), transparent 40%)',
      }} />
      <LeafField count={8} scope="section" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="text-center md:text-left max-w-xl">
          <h3
            className="font-extrabold text-3xl md:text-4xl leading-tight"
            style={{ fontFamily: fontDisplay }}
          >
            Let's design the
            <span className="italic text-amber-300"> yard you actually want</span>.
          </h3>
          <p className="text-emerald-50 mt-3 text-base md:text-lg">
            Free 90-minute consult on your property. Sketch, plant list, real numbers — yours to keep, with no commitment.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <a
            href="#contact"
            className="gl-cta-sheen inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-white text-emerald-800 font-extrabold text-lg shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all whitespace-nowrap"
          >
            <Calendar size={20} />Book my consult
          </a>
          <a
            href={`tel:${phoneRaw}`}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-900/40 hover:bg-emerald-900/60 backdrop-blur border border-white/20 text-white font-bold transition-colors whitespace-nowrap"
          >
            <Phone size={18} />{phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function Contact({ contact, hours, fontDisplay }) {
  const phoneRaw = contact.phone.replace(/[^\d+]/g, '');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitted(true);
  }

  return (
    <section id="contact" className="py-20 md:py-28 bg-[#FAFBF7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Reach out</p>
          <h2
            className="font-extrabold text-4xl md:text-5xl text-emerald-950 leading-tight mb-6"
            style={{ fontFamily: fontDisplay }}
          >
            Tell us about
            <span className="italic text-emerald-700"> your yard.</span>
          </h2>
          <p className="text-emerald-900/70 mb-8 leading-relaxed">
            Send a quick note and we'll set up a free 90-minute on-site consult. Real reply within one business day, no robo-quotes, no pressure.
          </p>

          {submitted ? (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-7 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-600 text-white flex items-center justify-center mb-4">
                <Check size={26} />
              </div>
              <p className="font-extrabold text-emerald-900 text-lg mb-2" style={{ fontFamily: fontDisplay }}>
                Got it, {form.name.split(' ')[0]}.
              </p>
              <p className="text-sm text-emerald-800 leading-relaxed">
                Mark or one of our designers will reach out within one business day to set up your consult.
              </p>
              <button
                type="button"
                onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', message: '' }); }}
                className="mt-4 text-sm text-emerald-700 font-semibold hover:text-emerald-900"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-emerald-900">Name</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-emerald-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none text-sm bg-white"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-emerald-900">Phone</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-emerald-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none text-sm bg-white"
                  />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-semibold text-emerald-900">Email</span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-emerald-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none text-sm bg-white"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-emerald-900">What are you thinking?</span>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Backyard redo, xeriscape conversion, weekly maintenance..."
                  className="mt-1 w-full px-3.5 py-2.5 rounded-lg border border-emerald-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 outline-none text-sm bg-white"
                />
              </label>
              <button
                type="submit"
                className="gl-cta-sheen w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold shadow-md hover:shadow-lg transition-all"
              >
                Request my free consult <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>

        <div>
          <div className="rounded-2xl border border-emerald-100 bg-white overflow-hidden shadow-sm">
            <div className="aspect-[16/10] bg-gradient-to-br from-emerald-100 via-amber-50 to-emerald-50 relative overflow-hidden">
              <div className="absolute inset-0 opacity-50" style={{
                backgroundImage:
                  'repeating-linear-gradient(0deg, rgba(20,83,45,0.10) 0 1px, transparent 1px 28px), repeating-linear-gradient(90deg, rgba(20,83,45,0.10) 0 1px, transparent 1px 28px)',
              }} aria-hidden="true" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <span className="w-14 h-14 rounded-2xl bg-emerald-700 text-white flex items-center justify-center mb-3 shadow-lg">
                  <MapPin size={22} />
                </span>
                <p className="font-bold text-emerald-950">{contact.address}</p>
                <p className="text-sm text-emerald-900/70 mt-1">Design office + 4-acre nursery · open to the public Sat AM</p>
              </div>
              <svg className="absolute bottom-4 left-1/2 -translate-x-1/2" viewBox="0 0 200 30" width="180" height="24" aria-hidden="true">
                <g fill="#15803D">
                  <circle cx="20" cy="15" r="3" />
                  <circle cx="60" cy="12" r="3" />
                  <circle cx="100" cy="18" r="3" />
                  <circle cx="140" cy="14" r="3" />
                  <circle cx="180" cy="16" r="3" />
                </g>
                <path d="M20,15 L60,12 L100,18 L140,14 L180,16" stroke="#15803D" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
              </svg>
            </div>
            <div className="divide-y divide-emerald-100">
              <ContactRow icon={Phone} href={`tel:${phoneRaw}`} label="Call us" value={contact.phone} />
              <ContactRow icon={Mail} href={`mailto:${contact.email}`} label="Email" value={contact.email} />
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-emerald-100 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-emerald-50/60 flex items-center gap-2">
              <Clock size={16} className="text-emerald-700" />
              <span className="font-bold text-emerald-900 text-sm uppercase tracking-wider">Office Hours</span>
            </div>
            {hours.map((h) => (
              <div
                key={h.day}
                className="flex items-center justify-between px-5 py-3 border-t border-emerald-100"
              >
                <span className="font-semibold text-emerald-950">{h.day}</span>
                <span className="text-emerald-900/70 font-medium">{h.time}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-5 flex items-start gap-3">
            <span className="w-10 h-10 rounded-lg bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
              <Lightbulb size={18} />
            </span>
            <div>
              <p className="font-bold text-amber-900 leading-tight">Pro tip</p>
              <p className="text-sm text-amber-900/80 mt-1 leading-relaxed">
                Book a winter consult — we have more time, you get free design work, and your yard is ready the minute the ground thaws.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, href, label, value }) {
  const inner = (
    <div className="flex items-center gap-4 group px-5 py-4">
      <span className="w-11 h-11 flex-shrink-0 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition-colors">
        <Icon size={18} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-wider text-emerald-900/60 font-bold">{label}</div>
        <div className="text-emerald-950 font-semibold">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : <div>{inner}</div>;
}

function Footer({ demo, fontDisplay }) {
  return (
    <footer className="bg-emerald-950 text-emerald-100 relative overflow-hidden">
      <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="block w-full h-10 text-emerald-950" aria-hidden="true">
        <path d="M0,0 C300,40 700,10 1100,30 C1300,40 1400,20 1440,30 L1440,60 L0,60 Z" fill="#FAFBF7" transform="translate(0,-30)" />
      </svg>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-14 pb-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white">
              <GreenLeafMark size={18} />
            </span>
            <span
              className="font-extrabold text-xl text-white"
              style={{ fontFamily: fontDisplay }}
            >
              {demo.name}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-emerald-200/80 mb-4">
            Family-run landscape design, install, and maintenance for the Front Range. Native plants from our own nursery. Six in-house crews.
          </p>
          <div className="flex items-center gap-1 text-xs text-emerald-300">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
            ))}
            <span className="ml-1.5 font-semibold">4.8 · 320+ Google reviews</span>
          </div>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            {demo.services.map((s) => (
              <li key={s.title}>
                <a href="#services" className="hover:text-white transition-colors">{s.title}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Visit the nursery</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <Phone size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <a href={`tel:${demo.contact.phone.replace(/[^\d+]/g, '')}`} className="hover:text-white">{demo.contact.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <a href={`mailto:${demo.contact.email}`} className="hover:text-white break-all">{demo.contact.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <span>{demo.contact.address}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-emerald-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-emerald-300/70">
          <p>&copy; {new Date().getFullYear()} {demo.name}. All rights reserved.</p>
          <p>
            Demo site built by{' '}
            <Link to="/" className="text-amber-300 font-semibold hover:text-white">Sudco Solutions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default GreenLeafLandscapingDemo;
